// ────────────────────────────────────────────────────────────────────────────
// Timezone helpers
// ────────────────────────────────────────────────────────────────────────────
// Core idea:
//   • The backend stores every timestamp in UTC.
//   • The frontend ALWAYS sends UTC ISO strings (with "Z" suffix) so the
//     backend never has to guess what timezone a naive "HH:mm" means.
//   • The DROPZONE timezone (from SystemConfig) is what users *think* in
//     when they type a time — e.g. a Canadian DZ means "9:00 AM Toronto
//     local" regardless of whether the operator is physically in Canada or
//     India. These helpers convert between DZ-local wall-clock time and
//     absolute UTC so both sides stay synchronized.
//
// No external deps used — pure Intl.DateTimeFormat.

/**
 * Convert a DZ-local wall-clock date and time into a UTC Date.
 *
 * Example:
 *   dzLocalToUtc('2026-04-15', '09:00', 'America/Toronto')
 *   // → Date representing 2026-04-15T13:00:00Z (9am Toronto in EDT)
 *
 * @param {string} dateStr   ISO date string "YYYY-MM-DD"
 * @param {string} timeStr   "HH:mm" or "HH:mm:ss"
 * @param {string} tzName    IANA zone, e.g. "America/Toronto". Empty / "UTC"
 *                           → treats input as UTC.
 * @returns {Date}
 */
export function dzLocalToUtc(dateStr, timeStr, tzName) {
  if (!dateStr || !timeStr) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi, s = 0] = timeStr.split(':').map(Number);

  if (!tzName || tzName === 'UTC') {
    return new Date(Date.UTC(y, mo - 1, d, h, mi, s || 0));
  }

  // Algorithm: treat the input as if it were UTC, then compute how the
  // target timezone would format that instant. The difference between the
  // formatted parts and the original input is the UTC offset of that tz
  // on that date (accounts for DST). Subtract to get the true UTC instant.
  const asIfUtc = new Date(Date.UTC(y, mo - 1, d, h, mi, s || 0));
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tzName,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(asIfUtc);

  const get = (t) => Number(parts.find((p) => p.type === t).value);
  const fy = get('year');
  const fmo = get('month');
  const fd = get('day');
  const fh = get('hour') === 24 ? 0 : get('hour');
  const fmi = get('minute');
  const fs = get('second');

  const formattedAsUtc = Date.UTC(fy, fmo - 1, fd, fh, fmi, fs);
  const offsetMs = formattedAsUtc - asIfUtc.getTime();

  // True UTC = input interpreted as tz-local, minus that tz's offset.
  return new Date(asIfUtc.getTime() - offsetMs);
}

/**
 * Convert a UTC Date / ISO string into the DZ-local wall-clock parts.
 * Use this when rendering times stored in UTC back in DZ local for display.
 *
 * Returns a plain object { date: "YYYY-MM-DD", time: "HH:mm" }.
 */
export function utcToDzLocal(utcInput, tzName) {
  const d = utcInput instanceof Date ? utcInput : new Date(utcInput);
  if (isNaN(d.getTime())) return { date: '', time: '' };
  const tz = tzName && tzName !== '' ? tzName : 'UTC';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour') === '24' ? '00' : get('hour')}:${get('minute')}`,
  };
}

/**
 * Current date in the DZ timezone, as "YYYY-MM-DD".
 * Useful as a default value for date pickers that should show "today at the DZ".
 */
export function todayInDz(tzName) {
  return utcToDzLocal(new Date(), tzName).date;
}

/**
 * Shift a "YYYY-MM-DD" calendar-day string by N days (negative = past).
 * Pure calendar arithmetic on the string's own Y/M/D via Date.UTC — no
 * browser or DZ timezone is ever consulted, so it is safe on DZ-local day
 * strings (e.g. `shiftDateStr(todayInDz(dzTz), -1)` = yesterday at the DZ).
 */
export function shiftDateStr(dateStr, days) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * Build a UTC ISO string ("YYYY-MM-DDTHH:mm:ss.SSSZ") from a DZ-local
 * date + time. This is what should be sent to any backend endpoint that
 * accepts a timestamp — the server parses it with plain `new Date()` and
 * gets the right instant regardless of its own timezone.
 */
export function dzLocalToUtcISO(dateStr, timeStr, tzName) {
  const d = dzLocalToUtc(dateStr, timeStr, tzName);
  return d ? d.toISOString() : null;
}

/**
 * Resolve any booking date value to its DZ-local calendar day ("YYYY-MM-DD").
 *
 * Handles the two shapes the API returns:
 *   • A date-only string ("YYYY-MM-DD") is ALREADY a DZ-local calendar day —
 *     returned verbatim. Slicing/parsing it as an instant would be wrong.
 *   • A full ISO instant ("...T21:00:00.000Z", from a Mongo `Date` field) is a
 *     UTC timestamp — it MUST be converted through the DZ timezone, never
 *     sliced. Slicing the raw ISO yields the UTC day, which is off-by-one for
 *     any DZ whose offset pushes the wall-clock day across midnight (the bug
 *     where a today-DZ booking was misclassified into History).
 *
 * Returns '' for missing or unparseable input so callers can route those rows
 * to a safe default (History) rather than leaving them stuck in Upcoming.
 *
 * @param {string|Date} raw     date-only string, ISO instant, or Date
 * @param {string}      tzName  IANA zone, e.g. "America/Toronto"
 * @returns {string} "YYYY-MM-DD" or ''
 */
export function dzCalendarDay(raw, tzName) {
  if (!raw) return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  return utcToDzLocal(raw, tzName).date;
}

/**
 * Returns a JS Date positioned at browser-local midnight of DZ-today's
 * calendar date. Use this for date-picker `selectedDate` state where the
 * downstream code derives a "YYYY-MM-DD" via `format(date, 'yyyy-MM-dd')`
 * (date-fns) or `toLocaleDateString` — both browser-tz formatters. Because
 * the Date object's browser-local YYYY-MM-DD equals DZ-today's YYYY-MM-DD,
 * the rest of the file works without needing any browser-tz changes.
 *
 * Examples (browser in IST, dropzone in Toronto, UTC clock 2026-05-02 03:00):
 *   - Browser today  = 2026-05-02 (IST)
 *   - DZ today       = 2026-05-01 (Toronto EDT, 23:00)
 *   - Returned Date  = 2026-05-01 00:00 (browser-local)
 *   - format(d, 'yyyy-MM-dd') = '2026-05-01' ✓ DZ-today
 */
export function dzTodayAsBrowserDate(tzName) {
  const dzDateStr = todayInDz(tzName);
  if (!dzDateStr) return new Date();
  const [y, m, d] = dzDateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * "When is this jump?" label for a booking row, DZ-timezone-safe across the
 * two scheduledDate shapes the API returns:
 *   • scheduledTimeSlot.start present (kiosk/slot-flow rows) — a real UTC
 *     instant → render date + time in the DZ timezone.
 *   • scheduledDate only (legacy public-flow rows) — stored as midnight UTC
 *     of the calendar date string, NOT a real instant. Formatting that raw
 *     value in a westward DZ tz (Toronto) rolls the label back a day, so
 *     render date-only from the encoded UTC calendar day, and never a time.
 *
 * @param {Object} booking  needs scheduledTimeSlot / scheduledDate
 * @param {string} tzName   IANA zone from SystemConfig.general.timezone
 * @param {string} [locale] BCP-47 tag for the rendered month/weekday names.
 *   Defaults to 'en-US' so every existing caller is byte-identical; the kiosk
 *   passes 'fr-CA' when the customer picked French so the whole surface —
 *   including these date labels — is in one language.
 * @returns {string|null}   e.g. "Fri, Jul 24, 9:00 AM" or "Fri, Jul 24"
 */
export function formatBookingWhen(booking, tzName, locale = 'en-US') {
  const tz = tzName && tzName !== '' ? tzName : 'UTC';
  const dateOpts = { weekday: 'short', month: 'short', day: 'numeric' };
  const start = booking?.scheduledTimeSlot?.start;
  if (start) {
    const d = new Date(start);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString(locale, {
        ...dateOpts, hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz,
      });
    }
  }
  if (!booking?.scheduledDate) return null;
  const d = new Date(booking.scheduledDate);
  if (isNaN(d.getTime())) return null;
  const isUtcMidnight =
    d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  if (isUtcMidnight) {
    // Legacy date-marker row: the calendar day lives in the UTC fields.
    return d.toLocaleDateString(locale, { ...dateOpts, timeZone: 'UTC' });
  }
  // Real instant without a slot — show date + time in DZ tz.
  return d.toLocaleString(locale, {
    ...dateOpts, hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz,
  });
}
